import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { actions as sharedActions, selectors as sharedSelectors } from "@shared/store";
import { selectors as hospitalSelectors } from "@containers/Hospital/store";
import { actions } from "@containers/User/store";
import { User } from "@shared/models";
import { UserForm } from "@containers/User/components";
import { MODAL_TYPES, STATUS } from "@shared/constants";
import { CreateUserDto } from "@containers/User/interfaces";
import { getFullName } from "@shared/utils";

interface UserCreateEditContainerProps {
  onClose: () => void;
  user: User | null;
}

const UserCreateEditContainer = (props: UserCreateEditContainerProps) => {
  const dispatch = useDispatch();
  const { onClose, user } = props;

  const roles = useSelector(sharedSelectors.getRoles());
  const authorizedUser = useSelector(sharedSelectors.getUserDetails());
  const hospital = useSelector(hospitalSelectors.getHospital());

  useEffect(() => {
    if (hospital?.id) {
      dispatch(sharedActions.getRoles.request({ hospital_id: hospital.id }));
    }
  }, [dispatch, hospital?.id]);

  const changeStatus = useCallback(
    (id: number) => {
      if (!user) return;
      dispatch(
        sharedActions.showModal({
          type: MODAL_TYPES.CONFIRM,
          props: {
            heading: user.is_active ? "Deactivate" : "Activate",
            content: `Are you sure you want to ${user.is_active ? "deactivate" : "activate"} ${getFullName(user)}?`,
            cancelBtnText: "Cancel",
            successBtnText: user.is_active ? "Deactivate" : "Activate",
            onSuccess: () => {
              dispatch(
                actions.updateUserStatus.request({
                  id,
                  status: user.is_active ? STATUS.INACTIVE : STATUS.ACTIVE,
                }),
              );

              onClose();
            },
          },
        }),
      );
    },
    [dispatch, onClose, user],
  );

  const changeFormValues = useCallback(() => {
    dispatch(sharedActions.updateOverlay({ leaveConfirm: true }));
  }, [dispatch]);

  const submitForm = useCallback(
    (payload: CreateUserDto) => {
      dispatch(sharedActions.hideOverlay());

      if (!user) {
        dispatch(actions.createUser.request(payload));
        return;
      }

      dispatch(actions.updateUser.request({ ...payload, id: Number(user.id) }));
    },
    [dispatch, user],
  );

  return (
    <div className="user-create-edit-container">
      <UserForm
        onChangeForm={changeFormValues}
        onCloseForm={onClose}
        onChangeStatus={changeStatus}
        onSubmitForm={submitForm}
        authorizedUser={authorizedUser}
        user={user}
        roles={roles}
        hospital={hospital}
      />
    </div>
  );
};

export default UserCreateEditContainer;
